/**
 * The account's library of fabs - the floors somebody designed, kept in
 * Firestore under the account that designed them.
 *
 * WHY FIRESTORE AND NOT localStorage. `floorPlan.ts` keeps plans in the
 * browser, which is right for the floor a student is standing in this
 * minute and wrong as a library: a plan saved on a classroom machine turns
 * up in the next student's menu, and the author who made it cannot see it
 * from their own laptop. A library belongs to a person. Each document lives
 * at `users/{uid}/fabs/{planId}`, the rules (firestore.rules) let that uid
 * and nobody else read or write it, and so a fab shows up in exactly one
 * place: its author's account, on whatever machine they sign in from. A
 * guest's anonymous account is a real uid and counts.
 *
 * THE LOCAL STORE STAYS. The hall builder and the layout read the active
 * plan synchronously at import, before any network round trip could land,
 * so `floorPlan.ts` remains the working copy: Open pulls a document into it
 * and rebuilds the hall, Save pushes the working copy up. This module never
 * touches the hall itself.
 *
 * LOADED LATE, LIKE AUTH. `firebase/firestore` is not small, and a student
 * who never opens the Fabs menu never needs it; the SDK arrives on first use
 * behind a dynamic import, on the app `Auth` already initialised.
 *
 * FAILS LEGIBLY. A dead network, a closed rule, a signed-out student: each
 * is a phase the menu can name rather than a spinner that never stops.
 */

import { auth } from '../auth/Auth'
import { t } from '../i18n/i18n'
import { wellFormed, type FloorPlan, type PlanHall } from './floorPlan'

/** What is written: the plan minus its id, which is the document's own. */
export interface FabDocument {
  name: string
  hall: PlanHall
  tools: string[]
  savedAt: number
}

/** The longest name the rules accept and the menu will show. */
export const NAME_MAX = 60

export function toDocument(plan: FloorPlan): FabDocument {
  return {
    name: plan.name.trim().slice(0, NAME_MAX) || plan.name,
    hall: plan.hall,
    tools: [...plan.tools],
    savedAt: plan.savedAt,
  }
}

/**
 * A document back into a plan, or null if it is not one. The rules hold
 * writes to the shape, but a document can predate a rule, and a menu that
 * crashes on one bad row loses the student every good one.
 */
export function fromDocument(id: string, data: unknown): FloorPlan | null {
  if (!data || typeof data !== 'object') return null
  const d = data as Partial<FabDocument>
  const plan = {
    id,
    name: d.name,
    hall: d.hall,
    tools: d.tools,
    savedAt: typeof d.savedAt === 'number' ? d.savedAt : 0,
  }
  return wellFormed(plan) ? plan : null
}

export type LibraryPhase =
  /** Nobody is signed in, so there is no library to show. */
  | 'signed-out'
  /** Signed in; nothing fetched yet. */
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error'

type Firestore = typeof import('firebase/firestore')

class FabLibrary {
  phase: LibraryPhase = 'signed-out'
  /** Newest first. Empty until `ready`. */
  fabs: FloorPlan[] = []
  error: string | null = null
  /** True while a save or delete is in flight - the menu greys its buttons. */
  busy = false

  private fs: { mod: Firestore; db: import('firebase/firestore').Firestore } | null = null
  private forUid: string | null = null
  private listeners = new Set<() => void>()

  constructor() {
    // An account change is a different library. Drop the cache rather than
    // show one student another's fabs for the frame before the refetch.
    auth.subscribe(() => {
      const uid = auth.account?.uid ?? null
      if (uid === this.forUid) return
      this.forUid = uid
      this.fabs = []
      this.error = null
      this.phase = uid ? 'idle' : 'signed-out'
      this.emit()
    })
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn)
    return () => {
      this.listeners.delete(fn)
    }
  }

  private emit(): void {
    for (const fn of [...this.listeners]) fn()
  }

  private async open(): Promise<{ mod: Firestore; db: import('firebase/firestore').Firestore; uid: string }> {
    await auth.start()
    const uid = auth.account?.uid
    if (!uid) throw new Error(t('menu.LibrarySignedOut'))
    if (!this.fs) {
      const [appMod, mod] = await Promise.all([import('firebase/app'), import('firebase/firestore')])
      this.fs = { mod, db: mod.getFirestore(appMod.getApp()) }
    }
    return { ...this.fs, uid }
  }

  /** Fetch the account's fabs. Safe to call repeatedly; the menu does. */
  async refresh(): Promise<void> {
    if (this.phase === 'loading') return
    this.phase = 'loading'
    this.error = null
    this.emit()
    try {
      const { mod, db, uid } = await this.open()
      const snap = await mod.getDocs(mod.collection(db, 'users', uid, 'fabs'))
      const fabs: FloorPlan[] = []
      snap.forEach((doc) => {
        const plan = fromDocument(doc.id, doc.data())
        if (plan) fabs.push(plan)
      })
      fabs.sort((a, b) => b.savedAt - a.savedAt)
      this.fabs = fabs
      this.phase = 'ready'
    } catch (err) {
      this.fail(err)
    }
    this.emit()
  }

  /** Write the plan as its own document; a second save of the same id replaces. */
  async save(plan: FloorPlan): Promise<void> {
    this.busy = true
    this.emit()
    try {
      const { mod, db, uid } = await this.open()
      const stamped = { ...plan, savedAt: Date.now() }
      await mod.setDoc(mod.doc(db, 'users', uid, 'fabs', plan.id), toDocument(stamped))
      this.fabs = [stamped, ...this.fabs.filter((f) => f.id !== plan.id)]
      this.phase = 'ready'
      this.error = null
    } catch (err) {
      this.fail(err)
      throw err
    } finally {
      this.busy = false
      this.emit()
    }
  }

  async remove(id: string): Promise<void> {
    this.busy = true
    this.emit()
    try {
      const { mod, db, uid } = await this.open()
      await mod.deleteDoc(mod.doc(db, 'users', uid, 'fabs', id))
      this.fabs = this.fabs.filter((f) => f.id !== id)
    } catch (err) {
      this.fail(err)
      throw err
    } finally {
      this.busy = false
      this.emit()
    }
  }

  private fail(err: unknown): void {
    this.phase = auth.account ? 'error' : 'signed-out'
    const code = (err as { code?: string })?.code
    this.error =
      code === 'permission-denied'
        ? t('menu.LibraryDenied')
        : code === 'unavailable'
          ? t('menu.LibraryOffline')
          : err instanceof Error
            ? err.message
            : String(err)
  }
}

export const fabLibrary = new FabLibrary()
